import React, { FC, useMemo, useState } from "react";
import { Box, Button, Chip, Icon, Modal, Notification, Text, TextField, toast } from "@telia/teddy";
import { API_URL_SEND_FEEDBACK } from "../../api";
import { retailerFetch } from "../../retailerFetch";
import "./ReceiptReviewModal.less";

interface Props {
    open: boolean;
    onOpenChange: (open: boolean) => void;
}

const MAX_DESCRIPTION_LENGTH = 500;
const REVIEW_OPTIONS = [
    { value: "very_dissatisfied", label: "Veldig misfornøyd", icon: "smiley-very-sad", score: 1 },
    { value: "dissatisfied", label: "Litt misfornøyd", icon: "smiley-sad", score: 2 },
    { value: "neutral", label: "Middels", icon: "smiley-neutral", score: 3 },
    { value: "satisfied", label: "Fornøyd", icon: "smiley-happy", score: 4 },
    { value: "very_satisfied", label: "Veldig fornøyd", icon: "smiley-very-happy", score: 5 },
] as const;

type ReviewValue = (typeof REVIEW_OPTIONS)[number]["value"];

const ReceiptReviewModal: FC<Props> = ({ open, onOpenChange }) => {
    const [selectedReview, setSelectedReview] = useState<ReviewValue | "">("");
    const [description, setDescription] = useState("");
    const [submitState, setSubmitState] = useState<"idle" | "sending" | "error">("idle");
    const [errorMessage, setErrorMessage] = useState("");

    const selectedReviewOption = useMemo(
        () => REVIEW_OPTIONS.find((option) => option.value === selectedReview),
        [selectedReview],
    );

    const resetForm = () => {
        setSelectedReview("");
        setDescription("");
        setSubmitState("idle");
        setErrorMessage("");
    };

    const getReviewPayload = () => {
        if (!selectedReviewOption) return;

        const trimmedDescription = description.trim().slice(0, MAX_DESCRIPTION_LENGTH);
        const filledStars = "★".repeat(selectedReviewOption.score);
        const emptyStars = "☆".repeat(5 - selectedReviewOption.score);

        const orderRef = new URLSearchParams(window.location.search).get("orderRef") ?? undefined;

        return {
            description: trimmedDescription,
            orderRef,
            feedbackType: "review",
            reviewRating: `${filledStars + emptyStars} (${selectedReviewOption.label})`,
        };
    };

    const sendReview = async () => {
        const payload = getReviewPayload();
        if (!payload) return;

        const response = await retailerFetch(API_URL_SEND_FEEDBACK, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
            keepalive: true,
        });

        if (!response.ok) {
            let serverError = "";
            try {
                const body = await response.json();
                if (body && typeof body.error === "string") {
                    serverError = body.error;
                }
            } catch {
                // non-JSON response, fall through to default message
            }
            throw new Error(serverError || "Kunne ikke sende tilbakemelding. Prøv igjen.");
        }
    };

    const handleClose = (nextOpen: boolean) => {
        if (!nextOpen) {
            if (selectedReviewOption && submitState === "idle") {
                void sendReview().catch(() => {
                    // Ignore errors when sending on close to avoid unhandled promise rejections
                });
            }
            resetForm();
        }
        onOpenChange(nextOpen);
    };

    const handleSubmit = async () => {
        if (!selectedReviewOption) return;

        setSubmitState("sending");
        setErrorMessage("");

        try {
            await sendReview();
            toast.success("Takk for tilbakemeldingen!", { position: "top-center" });
            resetForm();
            onOpenChange(false);
        } catch (err) {
            setErrorMessage(err instanceof Error ? err.message : "Kunne ikke sende tilbakemelding. Prøv igjen.");
            setSubmitState("error");
        }
    };

    return (
        <Modal open={open} onOpenChange={handleClose}>
            <Modal.Overlay className="receipt-review-modal">
                <Modal.Content className="receipt-review-modal__content" onOpenAutoFocus={(e) => e.preventDefault()}>
                    <Modal.Group variant="title">
                        <Modal.Title as="h3">Hvordan opplevde du Nexus?</Modal.Title>
                        <Modal.Close aria-label="lukk" slot="floating" />
                    </Modal.Group>
                    <Modal.Group direction="column">
                        {submitState === "error" && (
                            <Notification type="tile" variant="warning" mb="200">
                                <Notification.Icon />
                                <Notification.Text>{errorMessage}</Notification.Text>
                            </Notification>
                        )}

                        <Box mb="300">
                            <Chip
                                type="single"
                                value={selectedReview}
                                onValueChange={(value) => setSelectedReview((value as ReviewValue) || "")}
                                variant="choice"
                                className="receipt-review-modal__chip-group"
                            >
                                {REVIEW_OPTIONS.map((option) => (
                                    <Chip.Item key={option.value} value={option.value}>
                                        <span className="receipt-review-modal__chip-item">
                                            <Icon name={option.icon} size="md" />
                                            <span>{option.label}</span>
                                        </span>
                                    </Chip.Item>
                                ))}
                            </Chip>
                        </Box>

                        <Box mb="300">
                            <TextField>
                                <TextField.Label>Kommentar (valgfritt)</TextField.Label>
                                <TextField.Textarea
                                    value={description}
                                    onChange={(e) => setDescription(e.target.value.slice(0, MAX_DESCRIPTION_LENGTH))}
                                    maxLength={MAX_DESCRIPTION_LENGTH}
                                    rows={4}
                                />
                            </TextField>
                            <Text variant="additional-100" className="receipt-review-modal__counter">
                                {description.length}/{MAX_DESCRIPTION_LENGTH}
                            </Text>
                        </Box>

                        <Button
                            variant="primary"
                            fullWidth
                            onClick={handleSubmit}
                            disabled={!selectedReviewOption || submitState === "sending"}
                        >
                            {submitState === "sending" ? "Sender..." : "Gi tilbakemelding"}
                        </Button>
                    </Modal.Group>
                </Modal.Content>
            </Modal.Overlay>
        </Modal>
    );
};

export default ReceiptReviewModal;
